/* eslint-disable react/no-array-index-key */
import dayjs from 'dayjs';
import React from 'react';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import Card from 'partial/components/Card';
import Button from 'partial/components/Button';
import { toastWarning } from 'react-geek-toast';
import { formatNumber, usePersistState } from 'helper';
import { HiOutlineTrendingDown, HiOutlineTrendingUp } from 'react-icons/hi';
import { AiOutlineBarChart, AiOutlineLineChart } from 'react-icons/ai';
import FormInlineSelect from 'partial/form/FormInlineSelect';
import HistogramRange from 'partial/components/HistogramRange';
import BarChartSeries from 'modules/common/chart/BarChartSeries';
import LineChartSeries from 'modules/common/chart/LineChartSeries';
import { XIcon } from '@heroicons/react/outline';
import { buildId } from '../constants';
import { useCompare } from '../hooks';

// const KEY_OPTIONS = [
//   { label: 'Transaction', value: 'transaction' },
//   { label: 'Amount', value: 'amount' },
// ];

const COMPARE_OPTIONS = [
  { label: 'By Day', value: 'day' },
  { label: 'By Month', value: 'month' },
  { label: 'By Year', value: 'year' },
];

const COLORS = [
  '#2ecc71',
  '#3498db',
  '#f1c40f',
  '#e74c3c',
  '#9b59b6',
  '#34495e',
  '#16a085',
  '#d35400',
  '#F5AAF9',
  '#AAF9CC',
  '#933FE5',
  '#F9BDAA',
];

const MAP_ROWS = {
  day: [
    '12:00 AM',
    '01:00 AM',
    '02:00 AM',
    '03:00 AM',
    '04:00 AM',
    '05:00 AM',
    '06:00 AM',
    '07:00 AM',
    '08:00 AM',
    '09:00 AM',
    '10:00 AM',
    '11:00 AM',
    '12:00 PM',
    '01:00 PM',
    '02:00 PM',
    '03:00 PM',
    '04:00 PM',
    '05:00 PM',
    '06:00 PM',
    '07:00 PM',
    '08:00 PM',
    '09:00 PM',
    '10:00 PM',
    '11:00 PM',
  ],
  month: [
    '01',
    '02',
    '03',
    '04',
    '05',
    '06',
    '07',
    '08',
    '09',
    '10',
    '11',
    '12',
    '13',
    '14',
    '15',
    '16',
    '17',
    '18',
    '19',
    '20',
    '21',
    '22',
    '23',
    '24',
    '25',
    '26',
    '27',
    '28',
    '29',
    '30',
    '31',
  ],
  year: [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ],
};

const MAP_ROW_LABEL = {
  day: 'Hour',
  month: 'Day',
  year: 'Month',
};

// eslint-disable-next-line
const Th = ({ children, ...props }) => (
  <th
    className="sticky top-0 px-6 py-3 text-xs font-medium text-gray-900 uppercase tracking-wider"
    {...props}
  >
    {children}
  </th>
);
// eslint-disable-next-line
const Td = ({ children, ...props }) => (
  <th
    className="px-6 py-2 whitespace-nowrap text-sm font-medium text-gray-900"
    {...props}
  >
    {children}
  </th>
);

const renderCount = (row, c, data, mode, display_key, defaultValue = null) => {
  const raw = get(data, `table.${buildId(c, mode)}.${row}`) || {};
  return raw[`${display_key}`] || defaultValue;
};

const renderCompare = (curr, prev, compare) => {
  if (!prev || !curr) return null;
  const difference = ((curr - prev) / prev) * 100;
  const percentage = formatNumber(difference, 1);
  if (difference === 0) return null;
  const getClassName = () => {
    if (compare && compare === dayjs().format('YYYY-MM-DD'))
      return 'flex ml-2 text-yellow-500';
    if (difference > 0) return 'flex ml-2 text-green-700';
    if (difference < 0) return 'flex ml-2 text-danger-600';
    return 'flex ml-2 text-green-700';
  };
  const getIcon = () => {
    if (difference > 0) return <HiOutlineTrendingUp className="h-5 w-5 ml-1" />;
    if (difference < 0)
      return <HiOutlineTrendingDown className="h-5 w-5 ml-1" />;
    return null;
  };
  return (
    <span className={getClassName()}>
      {percentage}%{getIcon()}
    </span>
  );
};

const CompareValue = ({ row, col, prevCol, data, meta, defaultValue }) => {
  return (
    <div className="flex items-center justify-center">
      {renderCount(row, col, data, meta.compare, 'transaction', defaultValue)}
      {renderCompare(
        renderCount(row, col, data, meta.compare, 'transaction'),
        renderCount(row, prevCol, data, meta.compare, 'transaction')
      )}
    </div>
  );
};

CompareValue.defaultProps = {
  prevCol: null,
  defaultValue: null,
};

CompareValue.propTypes = {
  row: PropTypes.string.isRequired,
  col: PropTypes.string.isRequired,
  prevCol: PropTypes.oneOfType([
    PropTypes.instanceOf(Object),
    PropTypes.string,
  ]),
  data: PropTypes.instanceOf(Object).isRequired,
  meta: PropTypes.instanceOf(Object).isRequired,
  defaultValue: PropTypes.oneOfType([
    PropTypes.instanceOf(Object),
    PropTypes.string,
  ]),
};

function TransactionCompare() {
  const [chartType, setChartType] = React.useState('line');
  const toggleChart = React.useMemo(
    () => (
      <div className="grid grid-cols-1 sm:flex gap-1 items-center">
        <div className="space-x-1 flex justify-end">
          <Button
            size="sm"
            primary={chartType === 'bar'}
            outlinePrimary={chartType !== 'bar'}
            icon={AiOutlineBarChart}
            onClick={() => setChartType('bar')}
          />
          <Button
            size="sm"
            primary={chartType === 'line'}
            outlinePrimary={chartType !== 'line'}
            icon={AiOutlineLineChart}
            onClick={() => setChartType('line')}
          />
        </div>
      </div>
    ),
    [chartType, setChartType]
  );
  const [meta, setMeta] = usePersistState(
    'comparative-meta',
    {
      compare: 'month',
      key: 'transaction',
    },
    {
      keepOnUnmount: false,
    }
  );
  const [items, setItems] = usePersistState(
    'comparative-items',
    [dayjs('2022-03-01').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')],
    {
      keepOnUnmount: false,
    }
  );
  const rows = React.useMemo(() => MAP_ROWS[meta.compare], [meta.compare]);
  const rowLabel = React.useMemo(() => MAP_ROW_LABEL[meta.compare], [
    meta.compare,
  ]);
  const [, data, chart] = useCompare(meta.compare, items, 'transaction');
  // const handleClear = () => {
  //   setItems([]);
  // };
  const handleAddItem = () => {
    setItems((state) => {
      const val = dayjs().format('YYYY-MM-DD');
      const ALLOW_DUPLICATE = true;
      if (items.indexOf(val) > -1 && !ALLOW_DUPLICATE) {
        toastWarning('Already Added!');
        return state;
      }
      return state.concat([val]).sort();
    });
  };
  const handleChangeItem = (index) => (v) => {
    setItems((state) => {
      const newValue = [...state].slice(0); // clone object
      newValue[index] = dayjs(v().from).format('YYYY-MM-DD');
      return newValue;
    });
  };
  const handleRemoveItem = (index) => () => {
    setItems((state) => {
      const newValue = [...state].slice(0); // clone object
      newValue.splice(index, 1);
      return newValue;
    });
  };
  const renderControl = () => (
    <thead className="bg-gray-50">
      <tr>
        <Th>{rowLabel}</Th>
        {items.map((item, i) => (
          <Th key={`${item}-${i}`} style={{ backgroundColor: COLORS[i] }}>
            <div className="flex space-x-1 justify-center">
              <HistogramRange
                key={meta.compare}
                onChange={handleChangeItem(i)}
                defaultMode={meta.compare}
                defaultValue={item}
                switchMode={false}
                minDate="2022-03-01"
                maxDate={dayjs().format('YYYY-MM-DD')}
              />
              {i !== 0 && (
                <Button size="xs" icon={XIcon} onClick={handleRemoveItem(i)} />
              )}
            </div>
          </Th>
        ))}
        <Th>
          <Button
            onClick={handleAddItem}
            disabled={items.length === 12}
            className={items.length === 12 ? 'cursor-not-allowed' : ''}
          >
            Add
          </Button>
        </Th>
      </tr>
    </thead>
  );
  return (
    <div className="space-y-6 px-8 mb-5">
      <div className="flex space-x-2">
        {/* <FormInlineSelect
          name="key"
          onChange={setMeta}
          value={meta.key}
          options={KEY_OPTIONS}
          label=""
        /> */}
        <FormInlineSelect
          name="compare"
          onChange={setMeta}
          value={meta.compare}
          options={COMPARE_OPTIONS}
          label=""
        />
        {/* <Button onClick={handleClear} disabled={isLoading}>
          Clear
        </Button> */}
      </div>
      <Card label="Comparative" renderAction={toggleChart}>
        <div className="flex flex-col pr-3" style={{ height: 250 }}>
          {chartType === 'line' && (
            <LineChartSeries labels={rows} series={chart} colors={COLORS} />
          )}
          {chartType === 'bar' && (
            <BarChartSeries labels={rows} series={chart} colors={COLORS} />
          )}
        </div>
      </Card>
      <div className="flex flex-col">
        <div className="-my-2 overflow-auto">
          <div className="py-2 align-middle inline-block min-w-full">
            <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                {renderControl()}
                <tbody className="bg-white divide-y divide-gray-200">
                  {rows.map((row) => (
                    <tr key={row}>
                      <Td width="100px">{row}</Td>
                      {items.map((c, i) => (
                        <Td key={`${row}-${i}`}>
                          <CompareValue
                            row={row}
                            col={c}
                            prevCol={items[i - 1]}
                            data={data}
                            meta={meta}
                            defaultValue="-"
                          />
                        </Td>
                      ))}
                      <Td />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

TransactionCompare.propTypes = {};

export default TransactionCompare;
